import { useEffect, useState } from 'react';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { useSouls } from '@Hooks/useSouls';
import service from '@Service';
import FishSearchSelect, { FishOption } from '@Components/FishSearchSelect';

// Mirror of slugifySoulName() in apps/api/src/services/ai/snippet.service.ts —
// produces the human-readable identifier used in the merge tag.
const slugifySoulName = (name: string): string =>
  name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const fishToOption = (fish: any): FishOption => {
  const profile = fish?.fishProfiles?.[0] ?? {};
  const fullName = `${profile.fName ?? ''} ${profile.lName ?? ''}`.trim();
  const contact = profile.email || fish?.phoneNumber || fish?.id;
  return {
    value: fish.id,
    label: fullName ? `${fullName} (${contact})` : String(contact),
  };
};

interface AiSnippetPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Callback invoked with the merge tag string to insert into the active editor.
   * The caller is responsible for splicing it into the body/subject at the cursor.
   * Default tag form is `[[ai:<soulId>]]`; pass `html: true` to receive `{{ai:<soulId>}}`.
   */
  onInsert: (tag: string, soul: { id: string; name: string }) => void;
  /** Optional fishId to use for the preview API call. */
  previewFishId?: string;
  /** When true, output `{{ai:<soulId>}}` for HTML editors. */
  html?: boolean;
}

const AiSnippetPickerModal = ({
  open,
  onOpenChange,
  onInsert,
  previewFishId,
  html = false,
}: AiSnippetPickerModalProps) => {
  const { data: souls, status } = useSouls({ enabled: open });
  const [selectedSoulId, setSelectedSoulId] = useState<string>('');
  const [selectedFish, setSelectedFish] = useState<FishOption | null>(
    previewFishId ? { value: previewFishId, label: previewFishId } : null,
  );
  const [previewResult, setPreviewResult] = useState<{
    text: string;
    finishReason?: string;
    toolCallCount?: number;
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Hydrate the prefilled fishId (if the parent form supplied one) into a
  // proper option label by looking up the member.
  useEffect(() => {
    if (!open || !previewFishId || selectedFish?.value === previewFishId) return;
    let cancelled = false;
    (async () => {
      try {
        const fish = await service.readFishById(previewFishId);
        if (cancelled) return;
        if (fish?.id) setSelectedFish(fishToOption(fish));
      } catch {
        // leave the bare-id label in place
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, previewFishId, selectedFish?.value]);

  // Any agent can be used as an AI snippet — being a subagent is optional, not required.
  // Sort subagents below their parents so the list still reflects hierarchy.
  const availableSouls = [...(souls ?? [])].sort((a, b) => {
    if (!!a.parentSoulId !== !!b.parentSoulId) return a.parentSoulId ? 1 : -1;
    return a.name.localeCompare(b.name);
  });
  const selectedSoul = availableSouls.find((s) => s.id === selectedSoulId);

  const handlePreview = async () => {
    if (!selectedSoulId || !selectedFish?.value) {
      setPreviewError('Select an agent and a sample member to preview.');
      return;
    }
    setPreviewError(null);
    setPreviewResult(null);
    setPreviewLoading(true);
    try {
      const result = await service.getAiSnippetPreview(
        selectedSoulId,
        selectedFish.value,
      );
      setPreviewResult({
        text: result?.text ?? '',
        finishReason: (result as any)?.finishReason,
        toolCallCount: (result as any)?.toolCallCount,
      });
    } catch (err) {
      console.error('AI snippet preview failed', err);
      setPreviewError(
        err instanceof Error
          ? err.message
          : 'Preview failed. Check the API logs.',
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleInsert = () => {
    if (!selectedSoul) return;
    const slug = slugifySoulName(selectedSoul.name) || selectedSoul.id;
    const tag = html ? `{{ai:${slug}}}` : `[[ai:${slug}]]`;
    onInsert(tag, { id: selectedSoul.id, name: selectedSoul.name });
    setSelectedSoulId('');
    setPreviewResult(null);
    setPreviewError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Insert AI Snippet</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium text-zinc-700">
              Agent
            </label>
            {status === 'pending' ? (
              <div className="text-sm text-zinc-500">Loading agents…</div>
            ) : availableSouls.length === 0 ? (
              <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600">
                No agents configured. Create one in /agents to use AI snippets.
              </div>
            ) : (
              <select
                className="border-input mt-1 flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs"
                value={selectedSoulId}
                onChange={(e) => setSelectedSoulId(e.target.value)}
              >
                <option value="">— Select an agent —</option>
                {availableSouls.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.parentSoulId ? '↳ ' : ''}
                    {s.name}
                  </option>
                ))}
              </select>
            )}
            {selectedSoul?.description && (
              <p className="mt-2 text-xs text-zinc-500">
                {selectedSoul.description}
              </p>
            )}
            {selectedSoul && (
              <p className="mt-1 text-xs text-zinc-500">
                Will insert{' '}
                <code className="rounded bg-zinc-100 px-1 py-0.5">
                  {html ? '{{ai:' : '[[ai:'}
                  {slugifySoulName(selectedSoul.name) || selectedSoul.id}
                  {html ? '}}' : ']]'}
                </code>
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-zinc-700">
              Preview against member
            </label>
            <FishSearchSelect
              value={selectedFish}
              onChange={setSelectedFish}
            />
            <div className="mt-2 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                disabled={!selectedSoulId || !selectedFish?.value || previewLoading}
                onClick={handlePreview}
                loading={previewLoading}
              >
                Run preview
              </Button>
            </div>
            {previewError && (
              <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                {previewError}
              </div>
            )}
            {previewResult && previewResult.text && (
              <pre className="mt-2 max-h-48 overflow-auto rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm whitespace-pre-wrap">
                {previewResult.text}
              </pre>
            )}
            {previewResult && !previewResult.text && (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                The agent ran but returned no text.
                {typeof previewResult.toolCallCount === 'number' &&
                  ` Tool calls: ${previewResult.toolCallCount}.`}
                {previewResult.finishReason &&
                  ` Finish reason: ${previewResult.finishReason}.`}{' '}
                Check the agent's system prompt — it may be exhausting steps on
                tool calls without producing a final response, or the prompt may
                be asking for a non-text output.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="link">Cancel</Button>
          </DialogClose>
          <Button
            variant="primary"
            disabled={!selectedSoulId}
            onClick={handleInsert}
          >
            Insert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AiSnippetPickerModal;
